import type { ChatMessage, SuggestedAction } from "../types/redux";
import { setProjectId } from "../ProjectSlice_Store";
import { revertLoadingToSucceeded } from "../ResultsSlice_Store";
import { clearProjectSelections } from "../DraftSelectionSlice_Store";
import { invalidateDocumentPreview, refreshDocumentByName } from "../DocumentPreviewSlice_Store";
import {
  finalizeMessage as newFinalizeMessage,
  messageChunkReceived as newMessageChunkReceived,
} from "../streaming/streamingSlice";
import {
  addGenerationProgress,
  addMessage,
  clearTurnTodoPlan,
  setBotTyping,
  setTurnTodoPlan,
  setWorkflowStatus,
  unlockChatInput,
  updateMessageText,
} from "./chatSlice";
import { migrateMessageContent } from "./chatMigrations";
import { highestStatusSequenceByTurn } from "./chatModuleState";
import type { ActivityContext } from "./chatSliceTypes";

/** Bot Framework's streaming pattern signals accumulated-text mode via streamType. */
const ACCUMULATED_STREAM_TYPES = ["streaming", "final"];

/**
 * Phase 6/8: feed one streamed chunk into the streaming slice, creating the
 * placeholder chat message the UI renders into on the first chunk.
 */
export function handleChatResponseChunk(
  channelData: any,
  activityText: string,
  ctx: ActivityContext
): void {
  // Turn off typing indicator when first chunk arrives
  ctx.dispatch(setBotTyping(false));

  const { turnId, sequence, streamType } = channelData;

  if (!turnId || sequence === undefined || sequence === null) {
    console.error("❌ [CHUNK] Missing required fields:", { turnId, sequence });
    return;
  }

  const existingMessage = ctx
    .getState()
    ?.chatSlice?.messages?.find((msg: ChatMessage) => msg.turnId === turnId);

  if (!existingMessage) {
    // Placeholder bot message — its text is populated from the streaming system
    ctx.dispatch(addMessage({ sender: "chatbot", text: "", turnId }));
  }

  ctx.dispatch(
    newMessageChunkReceived({
      messageId: turnId,
      chunk: activityText,
      index: sequence,
      isAccumulatedText: ACCUMULATED_STREAM_TYPES.includes(streamType),
    })
  );

  if (streamType === "final") {
    console.log("[FINALIZE] Received final streaming chunk, finalizing message:", turnId);
    ctx.dispatch(newFinalizeMessage({ messageId: turnId }));
    // Unlock chat input since streaming is complete
    ctx.dispatch(unlockChatInput());
  }
}

/**
 * Phase 8: persist the streamed text onto the message.
 *
 * `displayedText` already has [[FACT:xxx]] placeholders from the streaming
 * parser; `fullText` still holds raw ^fid=xxx^ markers, so migrate it — without
 * that, prior-turn citations stop being clickable once a new turn overwrites
 * streaming state.
 */
function persistStreamedText(
  turnId: string,
  streamingMessage: any,
  suggestedActions: SuggestedAction[] | undefined,
  ctx: ActivityContext
): void {
  console.log("[FINALIZE] Dispatching finalizeMessage for turnId:", turnId);
  ctx.dispatch(newFinalizeMessage({ messageId: turnId }));

  const finalText =
    streamingMessage.displayedText || migrateMessageContent(streamingMessage.fullText || "");
  const messageIndex = ctx
    .getState()
    .chatSlice.messages.findIndex((msg: ChatMessage) => msg.turnId === turnId);

  // Always update suggested_actions if we have them, regardless of finalText
  if (messageIndex !== -1) {
    ctx.dispatch(
      updateMessageText({
        index: messageIndex,
        text: finalText || undefined,
        suggested_actions: suggestedActions,
      })
    );
  }
}

/**
 * Refresh any document the bot just rewrote. `autoRefresh` matches by name
 * because a doc-drafter edit can change the docId; otherwise invalidate by id.
 */
function applyPreviewRefreshes(suggestedActions: any, ctx: ActivityContext): void {
  console.log("[DealOS] 🔄 Processing suggested_actions for preview refresh:", suggestedActions);
  if (!suggestedActions || !Array.isArray(suggestedActions)) return;

  suggestedActions.forEach((action: any) => {
    if (action.type !== "preview_document" || !action.document?.docId) return;

    if (action.autoRefresh && action.document?.documentName) {
      ctx.dispatch(
        refreshDocumentByName({
          documentName: action.document.documentName,
          newDocument: action.document,
        })
      );
    } else {
      ctx.dispatch(invalidateDocumentPreview(action.document.docId));
    }
  });
}

/**
 * Turn teardown: unlock the composer, rescue the results panel from an
 * indefinite skeleton, and drop the paragraph selections the turn consumed.
 */
function completeTurn(ctx: ActivityContext): void {
  ctx.dispatch(unlockChatInput());

  // If results status is still "loading" at turn completion, no results_payload
  // was sent this turn (e.g. viability check failed). Revert to "succeeded" so
  // the previous deals/docs stay visible instead of loading skeletons.
  const resultsStatus = ctx.getState().resultsSlice?.status;
  if (resultsStatus === "loading") {
    ctx.dispatch(revertLoadingToSucceeded());
  }

  // Cleared only now, so the user could see which paragraphs were selected
  // while reading the response.
  const currentProjectId = ctx.getState()?.projectSlice?.currentProjectId;
  if (currentProjectId) {
    console.log(
      `🧹 [ChatSlice] Clearing paragraph selections after chat response complete for project ${currentProjectId}`
    );
    ctx.dispatch(clearProjectSelections({ projectId: currentProjectId }));
  }
}

/** The bot's final answer for a turn, streamed or not. */
export function handleChatResponse(
  channelData: any,
  activityText: string,
  ctx: ActivityContext
): void {
  ctx.dispatch(setBotTyping(false));

  const turnId = channelData.turnId;
  const suggestedActions = channelData.suggested_actions;
  const streamingMessage = ctx.getState()?.streaming?.messages?.[turnId];

  if (turnId && streamingMessage) {
    persistStreamedText(turnId, streamingMessage, suggestedActions, ctx);
  } else if (turnId) {
    // Non-streaming fallback: add message with suggested_actions if present
    ctx.dispatch(
      addMessage({
        sender: "chatbot",
        text: activityText || "",
        turnId,
        suggested_actions: suggestedActions,
      })
    );
  }

  applyPreviewRefreshes(suggestedActions, ctx);

  // Unlock input when stream completes (or immediately for non-streaming)
  if (channelData.streamStatus === "complete" || !streamingMessage) {
    completeTurn(ctx);
  }

  if (channelData.projectId) {
    ctx.dispatch(setProjectId(channelData.projectId));
  }
}

/**
 * Fire-and-forget delivery means a later status can arrive before an earlier
 * one; ignore anything at or behind the highest sequence seen for the turn.
 */
function isStaleStatus(turnId: string | undefined, statusSequence: number): boolean {
  if (!turnId) return false;

  const highestSeen = highestStatusSequenceByTurn.get(turnId) ?? -1;
  if (statusSequence <= highestSeen) {
    console.log(
      `⏭️ WORKFLOW_STATUS: Ignoring out-of-order status (seq=${statusSequence} <= highest=${highestSeen})`
    );
    return true;
  }
  highestStatusSequenceByTurn.set(turnId, statusSequence);
  return false;
}

/** Reflect the running action in the "thinking" indicator and progress bar. */
function showWorkflowStatus(status: any, turnId: string | undefined, ctx: ActivityContext): void {
  // Include turnId so the UI can check if there's already a message for THIS turn
  ctx.dispatch(
    setWorkflowStatus({
      label: status.label,
      action: status.action,
      state: status.state || "start",
      visible: true,
      turnId: turnId,
    })
  );

  if (status.action !== "generate_content" || status.state !== "start") return;

  const currentProjectId = window.store?.getState()?.projectSlice?.currentProjectId;
  if (currentProjectId) {
    ctx.dispatch(addGenerationProgress({ projectId: currentProjectId }));
  }
}

/** Progress reporting for the action the agent is currently running. */
export function handleWorkflowStatus(channelData: any, ctx: ActivityContext): void {
  const status = channelData.status || {};
  const turnId = channelData.turnId;
  const statusSequence = channelData.statusSequence ?? 0;

  if (isStaleStatus(turnId, statusSequence)) return;

  if (status.todoPlan && turnId) {
    ctx.dispatch(
      setTurnTodoPlan({ turnId, plan: status.todoPlan, scopeSummary: status.scopeSummary })
    );
  }

  if (status.label === "_clear_status_") {
    ctx.dispatch(setWorkflowStatus(null));
    return;
  }

  if (status.state === "complete") {
    ctx.dispatch(setWorkflowStatus(null));
    // Note: paragraph selections are cleared when the chat response arrives so
    // the user can still see them while the response is being generated.
    if (turnId) {
      ctx.dispatch(clearTurnTodoPlan({ turnId }));
    }
    return;
  }

  showWorkflowStatus(status, turnId, ctx);
}
